import React from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import get from 'get-value';
import {
    change,
    FieldArray,
    getFormInitialValues,
    getFormValues,
} from "redux-form";
import styled from "styled-components";
import {
    bvmDataTimeout,
    loadBvmData,
    notifyBvmError,
    notifyBvmWarning,
} from "../../../../../../../actions/hdi/actionBvm";
import { ticketConstants } from "../../../../../../../constants/form/formTicketConstants";
import { getIncidentType } from "../../../../../../../constants/Utils";
import buttonMessages from "../../../../../../../intl/common/buttonMessages";
import formTicketTypeMessages from "../../../../../../../intl/common/formTicketTypeMessages";
import formMessages from "../../../../../../../intl/hdi/formMessages";
import {
    IconAdd,
    IconDelete,
    PrimaryButton,
} from "../../../../../../common/Button";
import { InputFieldWithValidation } from "../../../../../../common/InputFieldWithValidation";
import InputMultipleInputField from "../../../../../../common/InputMultipleInputField";
import InputSelectFieldWithValidation from "../../../../../../common/InputSelectFieldWithValidation";
import {
    RedButton,
    RequiredSpan,
} from "../../../../../../common/StyledComponents";
import { TextareaFieldWithValidation } from "../../../../../../common/TextareaFieldWithValidation";
import {
    validEmailArray,
    validVin,
} from "../../../../../../common/validationCommons";
import {
    isEditorView,
    isVisibleForDepartment,
    isVisibleForCountry,
} from "../../../../Utils";

const StyledDiv = styled.div`
    border-radius: 5px;
`;

const Button = styled(PrimaryButton)`
    margin-right: 5px;
`;

const createOption = (label) => ({
    label,
    value: label,
});

const BVM_DATA_LOAD_TIMEOUT = 5000;
const MAX_CUSTOMER_AGREEMENTS_COUNT = 7;

const CUSTOMER_AGREEMENT_COL_WIDTH = "16rem";
const MODEL_COL_WIDTH = "26rem";
const VERSION_COL_WIDTH = "26rem";
const SCP_CODE_COL_WIDTH = "10rem";

class TicketSection extends React.Component {
    state = {
        inputValue: "",
        isBvmLoading: false,
        lastQueriedVin: null,
    };

    bvmTimeoutId = null;

    componentDidUpdate(prevProps) {
        if (!this.state.isBvmLoading) {
            return;
        }

        const {
            bvmPayload,
            bvmError,
            bvmWarning,
            notifyBvmError,
            notifyBvmWarning,
        } = this.props;
        const payloadArrived =
            bvmPayload && prevProps.bvmPayload !== bvmPayload;
        const errorArrived = bvmError && prevProps.bvmError !== bvmError;
        const warningArrived =
            bvmWarning && prevProps.bvmWarning !== bvmWarning;

        if (!payloadArrived && !errorArrived && !warningArrived) {
            return;
        }

        this.clearBvmTimeout();

        if (payloadArrived) {
            this.applyBvmPayload(bvmPayload);
        } else if (errorArrived) {
            notifyBvmError(bvmError);
        } else if (warningArrived) {
            // The socket middleware does NOT dispatch alerts for BVM commands,
            // so we surface a warn alert here. Room to also handle e.g.
            // BVM_VIN_NOT_FOUND specifically (inline validation, keep the VIN,
            // etc.) — see `isVinNotFoundInBvm` below.
            notifyBvmWarning(bvmWarning);
        }
        this.setState({ isBvmLoading: false });
    }

    componentWillUnmount() {
        this.clearBvmTimeout();
    }

    clearBvmTimeout = () => {
        if (this.bvmTimeoutId !== null) {
            clearTimeout(this.bvmTimeoutId);
            this.bvmTimeoutId = null;
        }
    };

    applyBvmPayload = (payload) => {
        const { change } = this.props;
        change(
            `${ticketConstants.FORM_TICKET_SECTION}.${ticketConstants.FORM_FIELD_MODEL}`,
            payload.model || "",
        );
        change(
            `${ticketConstants.FORM_TICKET_SECTION}.${ticketConstants.FORM_FIELD_VERSION}`,
            payload.version || "",
        );
        change(
            `${ticketConstants.FORM_TICKET_SECTION}.${ticketConstants.FORM_FIELD_SCP_CODE}`,
            payload.scpCode || "",
        );
    };

    handleLoadBvmData = () => {
        const { loadBvmData, bvmDataTimeout, formValues } = this.props;

        const vin =
            formValues?.[ticketConstants.FORM_TICKET_SECTION]?.[
                ticketConstants.FORM_FIELD_VIN
            ];
        if (!vin || validVin(vin)) {
            return;
        }

        this.clearBvmTimeout();
        this.setState({ isBvmLoading: true, lastQueriedVin: vin });

        loadBvmData(vin);

        this.bvmTimeoutId = setTimeout(() => {
            this.bvmTimeoutId = null;
            this.setState({ isBvmLoading: false });
            bvmDataTimeout();
        }, BVM_DATA_LOAD_TIMEOUT);
    };

    normalizeVin = (value) => (value ? value.toUpperCase() : value);

    renderCustomerAgreements = ({ fields }) => {
        const { initialValues, readOnly, viewType } = this.props;

        const ownerAgreements = get(initialValues, `${ticketConstants.FORM_TICKET_SECTION}.${ticketConstants.FORM_FIELD_CUSTOMER_AGREEMENTS}`, []);
        const isEditor = isEditorView(viewType);

        const canEdit = !readOnly && isEditor;

        const customerAgreementColStyle = {
            width: CUSTOMER_AGREEMENT_COL_WIDTH,
        };

        if(!fields.length) {
            fields.push({});
        }

        return (
            <>
                <div className="form-row">
                    <div
                        className="col-auto pl-0 pr-2 pb-1"
                        style={customerAgreementColStyle}
                    >
                        <label className="col-form-label pl-0 pt-0 pb-0 mb-0 font-weight-bold">
                            <FormattedMessage
                                {...formMessages[
                                    ticketConstants
                                        .FORM_LABEL_CUSTOMER_AGREEMENT
                                ]}
                            />
                        </label>
                    </div>
                </div>
                {fields.map((row, index) => (
                    <div className="form-row" key={row}>
                        <div
                            className="col-auto pl-0 pr-2 pb-2"
                            style={customerAgreementColStyle}
                        >
                            <InputFieldWithValidation
                                field={`${row}.${ticketConstants.FORM_FIELD_CUSTOMER_AGREEMENT}`}
                                maxLength={7}
                                isDisabled={readOnly || (canEdit && !!ownerAgreements[index])}
                                isSmall
                            />
                        </div>
                        <div className="col-auto pl-0 pb-2">
                            {!readOnly && index > 0 && !(canEdit && !!ownerAgreements[index]) && (
                                <RedButton
                                    type="button"
                                    className="btn btn-sm"
                                    title="Remove"
                                    onClick={() => fields.remove(index)}
                                >
                                    <IconDelete />
                                </RedButton>
                            )}
                        </div>
                    </div>
                ))}
                {!readOnly && fields.length < MAX_CUSTOMER_AGREEMENTS_COUNT && (
                    <div className="form-row">
                        <div className="col-auto pl-0 pb-2">
                            <PrimaryButton
                                type="button"
                                className="btn btn-sm"
                                onClick={() => fields.push({})}
                            >
                                <IconAdd />
                                {"\u00A0"}
                                <FormattedMessage {...buttonMessages.ADD} />
                            </PrimaryButton>
                        </div>
                    </div>
                )}
            </>
        );
    };

    handleChange = (value) => {
        const { change } = this.props;
        change(
            `${ticketConstants.FORM_TICKET_SECTION}.${ticketConstants.FORM_FIELD_COPY_EMAIL}`,
            value,
        );
    };

    handleInputChange = (inputValue) => {
        this.setState({ inputValue });
    };

    handleKeyDown = (event) => {
        const {
            change,
            formValues: {
                ticketSection: { copyEmail },
            },
        } = this.props;
        const { inputValue } = this.state;
        if (!inputValue) return;
        switch (event.key) {
            case "Enter":
            case "Tab":
                change(
                    `${ticketConstants.FORM_TICKET_SECTION}.${ticketConstants.FORM_FIELD_COPY_EMAIL}`,
                    [...copyEmail, createOption(inputValue)],
                );
                this.setState({
                    inputValue: "",
                });
                event.preventDefault();
                break;
            default: {
            }
        }
    };

    render() {
        const {
            initialValues,
            readOnly,
            reqFields,
            userRights,
            viewType,
            userCountry,
            userDetail,
            formValues,
        } = this.props;

        const solutionGroup = get(formValues, `${ticketConstants.FORM_TEMPLATE_SECTION}.${ticketConstants.FORM_FIELD_SOLUTION_GROUP}`);
        const topic = get(formValues, `${ticketConstants.FORM_TICKET_SECTION}.${ticketConstants.FORM_FIELD_TEMPLATE_TYPE}`);
        const vin = get(formValues, `${ticketConstants.FORM_TICKET_SECTION}.${ticketConstants.FORM_FIELD_VIN}`);
        const initVin = get(initialValues, `${ticketConstants.FORM_TICKET_SECTION}.${ticketConstants.FORM_FIELD_VIN}`);
        const model = get(initialValues, `${ticketConstants.FORM_TICKET_SECTION}.${ticketConstants.FORM_FIELD_MODEL}`);
        const version = get(initialValues, `${ticketConstants.FORM_TICKET_SECTION}.${ticketConstants.FORM_FIELD_VERSION}`);
        const scp = get(initialValues, `${ticketConstants.FORM_TICKET_SECTION}.${ticketConstants.FORM_FIELD_SCP_CODE}`);

        const isEditor = isEditorView(viewType);
        const vinValid = !!vin && validVin(vin) === undefined;

        const { inputValue, isBvmLoading, lastQueriedVin } = this.state;
        const isVinAlreadyQueried = vinValid && vin === lastQueriedVin;

        const vinLocked = isEditor && !!initVin;
        const modelLocked = isEditor && !!model;
        const versionLocked = isEditor && !!version;
        const scpLocked = isEditor && !!scp;

        return (
            <>
                <StyledDiv className="border border-secondary px-4 py-2 bg-white">
                    {!isVisibleForCountry(userCountry) && isEditor && (
                        <div className="form-row">
                            <div className="col pb-2">
                                <InputSelectFieldWithValidation
                                    label={
                                        isVisibleForCountry(userDetail.group)
                                            ? formMessages[
                                                  ticketConstants
                                                      .FORM_LABEL_TYPE_REQUEST
                                              ]
                                            : formMessages[
                                                  ticketConstants
                                                      .FORM_LABEL_TYPE_INCIDENT
                                              ]
                                    }
                                    field={
                                        ticketConstants.FORM_FIELD_TYPE_INCIDENT
                                    }
                                    options={getIncidentType}
                                    intlMessages={formTicketTypeMessages}
                                    isDisabled={readOnly}
                                    isSmall
                                />
                            </div>
                        </div>
                    )}
                    {isVisibleForDepartment(solutionGroup, topic) && (
                        <>
                            <FieldArray
                                name={
                                    ticketConstants.FORM_FIELD_CUSTOMER_AGREEMENTS
                                }
                                component={this.renderCustomerAgreements}
                            />
                            <div className="form-row">
                                <div className="col pb-2">
                                    <InputFieldWithValidation
                                        label={
                                            formMessages[
                                                ticketConstants.FORM_LABEL_VIN
                                            ]
                                        }
                                        field={ticketConstants.FORM_FIELD_VIN}
                                        validations={[validVin]}
                                        normalize={this.normalizeVin}
                                        maxLength={17}
                                        isDisabled={readOnly || vinLocked}
                                        isRequired={
                                            reqFields.includes(
                                                ticketConstants.FORM_FIELD_VIN,
                                            ) && !readOnly
                                        }
                                        isSmall
                                    />
                                </div>
                                {!readOnly && (
                                    <div className="col-auto pb-2">
                                        <label
                                            className="col-form-label pl-0 font-weight-bold"
                                            aria-hidden="true"
                                        >
                                            {"\u00A0"}
                                        </label>
                                        <div>
                                            <Button
                                                type="button"
                                                className="btn btn-sm"
                                                disabled={
                                                    isBvmLoading ||
                                                    !vinValid ||
                                                    isVinAlreadyQueried ||
                                                    vinLocked
                                                }
                                                onClick={this.handleLoadBvmData}
                                            >
                                                <FormattedMessage
                                                    {...formMessages[
                                                        ticketConstants
                                                            .FORM_LABEL_LOAD_DATA
                                                    ]}
                                                />
                                                {isBvmLoading ? (
                                                    <span
                                                        className="spinner-border spinner-border-sm ml-2"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />
                                                ) : null}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="form-row">
                                <div className="col-auto pl-0 pr-2 pb-1">
                                    <label
                                        className="col-form-label pl-0 pt-0 pb-0 mb-0 font-weight-bold"
                                        style={{ whiteSpace: "nowrap" }}
                                    >
                                        <FormattedMessage
                                            {...formMessages[
                                                ticketConstants.FORM_LABEL_MODEL
                                            ]}
                                        />
                                        {" / "}
                                        <FormattedMessage
                                            {...formMessages[
                                                ticketConstants
                                                    .FORM_LABEL_VERSION
                                            ]}
                                        />
                                        {" / "}
                                        <FormattedMessage
                                            {...formMessages[
                                                ticketConstants
                                                    .FORM_LABEL_SCP_CODE
                                            ]}
                                        />
                                    </label>
                                </div>
                            </div>
                            <div className="form-row">
                                <div
                                    className="col-auto pl-0 pr-2 pb-2"
                                    style={{ width: MODEL_COL_WIDTH }}
                                >
                                    <InputFieldWithValidation
                                        field={`${ticketConstants.FORM_FIELD_MODEL}`}
                                        maxLength={50}
                                        isDisabled={readOnly || modelLocked}
                                        isSmall
                                    />
                                </div>
                                <div
                                    className="col-auto pl-0 pr-2 pb-2"
                                    style={{ width: VERSION_COL_WIDTH }}
                                >
                                    <InputFieldWithValidation
                                        field={`${ticketConstants.FORM_FIELD_VERSION}`}
                                        maxLength={50}
                                        isDisabled={readOnly || versionLocked}
                                        isSmall
                                    />
                                </div>
                                <div
                                    className="col-auto pl-0 pr-2 pb-2"
                                    style={{ width: SCP_CODE_COL_WIDTH }}
                                >
                                    <InputFieldWithValidation
                                        field={`${ticketConstants.FORM_FIELD_SCP_CODE}`}
                                        maxLength={15}
                                        isDisabled={readOnly || scpLocked}
                                        isSmall
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    <div className="form-row">
                        <div className="col pb-2">
                            <InputMultipleInputField
                                placeholder=""
                                label={
                                    formMessages[
                                        ticketConstants.FORM_LABEL_COPY_EMAIL
                                    ]
                                }
                                field={ticketConstants.FORM_FIELD_COPY_EMAIL}
                                validations={[validEmailArray]}
                                inputValue={inputValue}
                                isMulti={true}
                                isClearable={true}
                                isSearchable={false}
                                isDisabled={readOnly}
                                onHandleChange={this.handleChange}
                                onHandleInputChange={this.handleInputChange}
                                onHandleKeyDown={this.handleKeyDown}
                                isRequired={
                                    reqFields.includes(
                                        ticketConstants.FORM_FIELD_COPY_EMAIL,
                                    ) && !readOnly
                                }
                                isSmall
                            />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="col pb-2">
                            <InputFieldWithValidation
                                label={
                                    formMessages[
                                        ticketConstants.FORM_LABEL_SUBJECT
                                    ]
                                }
                                field={ticketConstants.FORM_FIELD_SUBJECT}
                                isDisabled={readOnly || userRights.canEdit}
                                isRequired={
                                    reqFields.includes(
                                        ticketConstants.FORM_FIELD_SUBJECT,
                                    ) && !readOnly
                                }
                                isSmall
                            />
                        </div>
                    </div>
                    {!isVisibleForCountry(userCountry) && (
                        <div className="form-row">
                            <div className="col pb-2">
                                <InputFieldWithValidation
                                    label={
                                        formMessages[
                                            ticketConstants.FORM_LABEL_ALIAS
                                        ]
                                    }
                                    field={ticketConstants.FORM_FIELD_ALIAS}
                                    isDisabled={readOnly}
                                    isRequired={
                                        reqFields.includes(
                                            ticketConstants.FORM_FIELD_ALIAS,
                                        ) && !readOnly
                                    }
                                    isSmall
                                />
                            </div>
                        </div>
                    )}
                    {isEditorView(viewType) && (
                        <div className="form-row">
                            <div className="col pb-2">
                                <InputFieldWithValidation
                                    label={
                                        formMessages[
                                            ticketConstants.FORM_LABEL_COMMENT
                                        ]
                                    }
                                    field={ticketConstants.FORM_FIELD_COMMENT}
                                    isDisabled={readOnly}
                                    isRequired={
                                        reqFields.includes(
                                            ticketConstants.FORM_FIELD_COMMENT,
                                        ) && !readOnly
                                    }
                                    isSmall
                                />
                            </div>
                        </div>
                    )}
                    <div className="form-row">
                        <div className="col-sm-12 pb-2">
                            <TextareaFieldWithValidation
                                label={
                                    formMessages[
                                        ticketConstants.FORM_LABEL_DESCRIPTION
                                    ]
                                }
                                field={ticketConstants.FORM_FIELD_DESCRIPTION}
                                rows={10}
                                isDisabled={readOnly || userRights.canEdit}
                                isRequired={
                                    reqFields.includes(
                                        ticketConstants.FORM_FIELD_DESCRIPTION,
                                    ) && !readOnly
                                }
                            />
                        </div>
                    </div>
                </StyledDiv>
            </>
        );
    }
}

const mapStateToProps = (state, props) => ({
    roles: state.profile.userDetail.roles,
    initialValues: getFormInitialValues(props.form)(state, `ticketSection`),
    userDetail: state.profile.userDetail,
    formValues: getFormValues(props.form)(state),
    bvmPayload: state.ticket.bvm.payload,
    bvmError: state.ticket.bvm.error,
    bvmWarning: state.ticket.bvm.warning,
});

const mapDispatchToProps = (dispatch, props) => ({
    change: (name, value) => dispatch(change(props.form, name, value)),
    loadBvmData: (vin) => dispatch(loadBvmData(vin)),
    bvmDataTimeout: () => dispatch(bvmDataTimeout()),
    notifyBvmError: (error) => dispatch(notifyBvmError(error)),
    notifyBvmWarning: (warning) => dispatch(notifyBvmWarning(warning)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TicketSection);

